import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck, faTrash, faCheckDouble, faBell } from '@fortawesome/free-solid-svg-icons';

// Import the notification service as a whole instead of individual functions
import notificationService from '../services/notificationService';
import UserAvatar from '../components/UserAvatar';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState('all'); // 'all' or 'unread'
  
  const limit = 20; // Items per page

  useEffect(() => {
    loadNotifications();
  }, [currentPage, filter]);

  const loadNotifications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const offset = (currentPage - 1) * limit;
      const unreadOnly = filter === 'unread';
      
      const response = await notificationService.fetchNotifications({
        limit,
        offset,
        unreadOnly
      });
      
      setNotifications(response.notifications || []);
      setTotalCount(response.total_count || 0);
      setUnreadCount(response.unread_count || 0);
    } catch (error) {
      setError('Failed to load notifications. Please try again later.');
      // Set empty data in case of error
      setNotifications([]);
      setTotalCount(0);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllNotificationsAsRead();
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => ({
          ...notification,
          is_read: true
        }))
      );
      setUnreadCount(0);
    } catch (error) {
      setError('Failed to mark all as read. Please try again.');
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markNotificationAsRead(notificationId);
      
      // Update local state
      setNotifications(prevNotifications =>
        prevNotifications.map(notification =>
          notification.id === notificationId
            ? { ...notification, is_read: true }
            : notification
        )
      );
      
      setUnreadCount(prevCount => Math.max(0, prevCount - 1));
    } catch (error) {
      setError('Failed to mark notification as read. Please try again.');
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Remove from local state
      setNotifications(prevNotifications =>
        prevNotifications.filter(notification => notification.id !== notificationId)
      );
      
      // Update counts
      setTotalCount(prevCount => prevCount - 1);
      const deletedNotification = notifications.find(n => n.id === notificationId);
      if (deletedNotification && !deletedNotification.is_read) {
        setUnreadCount(prevCount => Math.max(0, prevCount - 1));
      }
    } catch (error) {
      setError('Failed to delete notification. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const totalPages = Math.ceil(totalCount / limit);
  
  // Function to get appropriate link based on notification type
  const getNotificationLink = (notification) => {
    if (notification.reference_type === 'event' && notification.reference_id) {
      return `/dashboard?event=${notification.reference_id}`;
    } else if (notification.reference_type === 'community' && notification.reference_id) {
      return `/community/${notification.reference_id}`;
    }
    return '#';
  };

  return (
    <div className="container mt-3 mb-5">
      <div className="d-flex align-items-center mb-3">
        <Link to="/" className="btn btn-sm btn-outline-secondary me-3">
          <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
          Nazaj
        </Link>
        <h2 className="m-0">Obvestila</h2>
      </div>
      
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="btn-group" role="group">
            <button 
              type="button"
              className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('all')}
            >
              Vsa ({totalCount})
            </button>
            <button 
              type="button"
              className={`btn btn-sm ${filter === 'unread' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('unread')}
            >
              Neprebrana ({unreadCount})
            </button>
          </div>
          
          {unreadCount > 0 && (
            <button 
              className="btn btn-sm btn-outline-primary"
              onClick={handleMarkAllAsRead}
            >
              <FontAwesomeIcon icon={faCheckDouble} className="me-1" />
              Označi vse kot prebrano
            </button>
          )}
        </div>
        
        <div className="card-body p-0">
          {error && (
            <div className="alert alert-danger m-3">
              {error}
            </div>
          )}
          
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : notifications.length > 0 ? (
            <div className="list-group list-group-flush">
              {notifications.map(notification => (
                <div key={notification.id} className="list-group-item list-group-item-action d-flex align-items-start notification-item">
                  <div className="notification-avatar me-3">
                    {notification.actor ? (
                      <UserAvatar
                        profileImage={notification.actor.profile_image}
                        displayName={notification.actor.display_name}
                        email={notification.actor.email}
                        size={40}
                        avatarOnly={true}
                      />
                    ) : (
                      <div
                        className="rounded-circle d-flex justify-content-center align-items-center"
                        style={{
                          width: 40,
                          height: 40,
                          backgroundColor: '#6c757d',
                          color: 'white'
                        }}
                      >
                        <FontAwesomeIcon icon={faBell} />
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1" onClick={() => handleMarkAsRead(notification.id)}>
                    <Link
                      to={getNotificationLink(notification)}
                      className={`notification-link ${!notification.is_read ? 'fw-bold' : ''}`}
                    >
                      <div className="notification-content">
                        {notification.content}
                        {!notification.is_read && (
                          <span className="ms-2 badge bg-primary">Novo</span>
                        )}
                      </div>
                      <div className="text-muted small">
                        <span>{formatDate(notification.created_at)}</span>
                        <span className="ms-2 badge bg-secondary">{notification.type ? notification.type.replace(/_/g, ' ') : 'obvestilo'}</span>
                      </div>
                    </Link>
                  </div>
                  <div className="notification-actions ms-2">
                    {!notification.is_read && (
                      <button
                        className="btn btn-sm btn-outline-primary me-1"
                        onClick={() => handleMarkAsRead(notification.id)}
                        title="Označi kot prebrano"
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDeleteNotification(notification.id)}
                      title="Izbriši"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-4">
              <p className="mb-0">Ni obvestil</p>
              {filter === 'unread' && (
                <button 
                  className="btn btn-link"
                  onClick={() => setFilter('all')}
                >
                  Prikaži vsa obvestila
                </button>
              )}
            </div>
          )}
        </div>
        
        {totalPages > 1 && (
          <div className="card-footer d-flex justify-content-center">
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  >
                    Prejšnja
                  </button>
                </li>
                
                {Array.from({length: totalPages}, (_, i) => i + 1).map(page => (
                  <li 
                    key={page} 
                    className={`page-item ${currentPage === page ? 'active' : ''}`}
                  >
                    <button 
                      className="page-link"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  >
                    Naslednja
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;